import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { isNullish } from '@chainflip/utils/guard';
import { getBoostPoolCacheQuery } from '@/shared/graphql/cache/boost';
import { type GetBoostPoolCacheQuery } from '@/shared/graphql/generated/cache/graphql';
import { type GetBoostSharesByPoolQuery } from '@/shared/graphql/generated/lp/graphql';
import { getBoostSharesByPoolQuery } from '@/shared/graphql/lp/boost';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';

export type BoostBalance = NonNullable<
  GetBoostPoolCacheQuery['pools']
>['nodes'][number]['balances']['nodes'][number];

export type BoostSharesMap = Map<
  string,
  NonNullable<
    GetBoostSharesByPoolQuery['pools']
  >['nodes'][number]['boostSummaries']['nodes'][number]
>;

export const useBoostPool = (asset: ChainflipAsset | undefined, fee: number | undefined) => {
  const { data: cachePool, isLoading: isCacheBoostPoolIdLoading } = useGqlQuery(
    getBoostPoolCacheQuery,
    {
      variables: { chainflipAsset: asset, feeTierPips: fee },
      context: { clientName: 'statechainCache' },
      select: (queryResult: GetBoostPoolCacheQuery) => queryResult.pools?.nodes[0],
    },
  );

  const { data: boostSharesData, isLoading: isBoostSharesLoading } = useGqlQuery(
    getBoostSharesByPoolQuery,
    {
      variables: {
        asset,
        feeTierPips: fee,
      },
      select: (queryResult: GetBoostSharesByPoolQuery) =>
        new Map(
          queryResult.pools?.nodes[0].boostSummaries.nodes.map((summary) => [
            summary.account!.idSs58,
            summary,
          ]),
        ),
      enabled: Boolean(asset) && !isNullish(fee),
      context: { clientName: 'lpProcessor' },
    },
  );

  return {
    isLoading: isCacheBoostPoolIdLoading || isBoostSharesLoading,
    isCacheBoostPoolIdLoading,
    boostPoolOverview: cachePool,
    boostBalances: cachePool?.balances?.nodes ?? [],
    boostSharesAggregates: boostSharesData ?? new Map(),
  };
};
